/* =============================================================================
   SMOKEY'S CANONICAL CART CONTEXT  (one promise-locked guard)
   =============================================================================
   During the PURP <-> Greenline location migration the cart carries TWO identity
   attributes and both are load-bearing:

     purp_location_id = canonical PURP location id (new storefront state)
     store_id         = Greenline alias, the ORDER-OWNERSHIP cutover signal that
                        decides whether PURP or the legacy connector owns the order

   Stamping hidden inputs inside individual /cart/add forms is not a sufficient
   contract: any path that misses them (or races the repair) can create an order
   whose ownership signal is absent or stale. So EVERY add-to-cart path must await
   ONE verified repair first.

   Contract of ensureCanonicalCartContext():
     resolve a KNOWN canonical identity   (else reject + reopen the selector)
     -> derive its exact Greenline alias  (else reject: fail closed)
     -> read the cart
     -> if either attribute is wrong/missing, repair BOTH in one /cart/update.js
     -> re-read and VERIFY both values
     -> only then resolve, permitting /cart/add

   Guarantees:
     * cart lines are never touched (attributes-only update)
     * delivery / express selections are preserved (other attributes are not sent,
       and Shopify only updates the keys provided)
     * a PURP<->Greenline normalization of the SAME store never clears the cart
     * fails closed: on any failure the add is NOT released
     * one promise lock, so simultaneous quick-add / rail / product-form /
       predictive-search gestures collapse into a single repair
   ========================================================================== */
(function () {
  'use strict';

  if (window.__smokeysCartContextInstalled) return;
  window.__smokeysCartContextInstalled = true;

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var inFlight = null;   // the in-flight promise lock (deduplication ONLY)

  // NOTE: there is deliberately NO durable "already authorized" cache. Another
  // theme script, an app, a cart restore or a checkout transition can change cart
  // attributes without touching localStorage/cookies or firing storeSelected, so a
  // cached authorization could wave an add past the final fence without ever
  // looking at the real cart. Every add/checkout performs a fresh read; a GET
  // /cart.js is cheap next to importing an order into the wrong ownership path.

  function L() { return window.SmokeysLocation; }

  function err(code, message) {
    var e = new Error(message);
    e.code = code;
    e.userMessage = message;
    return e;
  }

  function readCart() {
    return nativeFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw err('cart_read_failed', 'We could not read your cart. Please try again.');
      return r.json();
    });
  }

  function writeAttributes(canonicalId, legacyId) {
    return nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      // ONLY these keys are sent, so delivery/express attributes and every cart
      // line are preserved.
      body: JSON.stringify({
        attributes: { purp_location_id: canonicalId, store_id: legacyId },
      }),
    }).then(function (r) {
      if (!r.ok) throw err('cart_update_failed', 'We could not confirm your store. Please try again.');
      return r.json();
    });
  }

  function attributesMatch(cart, canonicalId, legacyId) {
    var a = (cart && cart.attributes) || {};
    return String(a.purp_location_id || '') === String(canonicalId) &&
           String(a.store_id || '') === String(legacyId);
  }

  function performCartContextVerification() {
    return Promise.resolve().then(function () {
      // A store switch empties the cart and rewrites its attributes. Releasing an
      // add mid-transition could attach a line to the store being left behind.
      if (window.__SMOKEYS_STORE_TRANSITION) {
        throw err('store_transition', 'Switching stores — please try again in a moment.');
      }

      var loc = L();
      if (!loc || !loc.isConfigValid || !loc.isConfigValid()) {
        throw err('config_invalid', 'Store locations are unavailable right now. Please refresh and try again.');
      }

      // 1. KNOWN canonical identity, or fail closed + reopen the selector.
      // Seed from the IMMUTABLE server value (cart attributes), so a returning
      // cart-only session — correct cart attributes but no localStorage/cookie
      // after a privacy clear or session restore — still resolves.
      var seed = '';
      try {
        seed = window.__SMOKEYS_SERVER_STORE_SEED || window.storeId || '';
      } catch (e) {}
      var canonicalId = '';
      try { canonicalId = loc.currentId(seed) || ''; } catch (e) {}
      if (!canonicalId) {
        try {
          if (typeof window.openStoreLocator === 'function') window.openStoreLocator();
        } catch (e) {}
        throw err('no_store_selected', 'Please choose your Smokey’s before adding to cart.');
      }

      // 2. Exact Greenline alias for order ownership. No alias -> fail closed.
      var legacyId = '';
      try { legacyId = String(loc.legacyConsumerId(canonicalId) || ''); } catch (e) {}
      if (!legacyId) {
        throw err('no_legacy_alias', 'We could not confirm your store. Please try again.');
      }

      // Ownership contradiction: both attributes known but different stores.
      // smokeys-store-context emits blank in that case, so canonicalId would be
      // blank too unless another source resolved — surface it explicitly here for
      // any direct caller and reopen the selector rather than guessing.
      function conflicting(cart) {
        var a = (cart && cart.attributes) || {};
        if (!a.purp_location_id || !a.store_id) return false;
        var p = loc.canonicalize(a.purp_location_id);
        var l = loc.canonicalize(a.store_id);
        return !!p && !!l && p !== l;
      }

      // 3-5. Read, repair if needed, then VERIFY by reading back.
      return readCart().then(function (cart) {
        if (conflicting(cart)) {
          try {
            if (typeof window.openStoreLocator === 'function') window.openStoreLocator();
          } catch (e) {}
          throw err('store_conflict', 'Your store changed. Please choose your Smokey’s again.');
        }
        if (attributesMatch(cart, canonicalId, legacyId)) return cart;
        return writeAttributes(canonicalId, legacyId).then(function () {
          return readCart();
        });
      }).then(function (cart) {
        if (!attributesMatch(cart, canonicalId, legacyId)) {
          throw err('cart_verify_failed', 'We could not confirm your store. Please try again.');
        }
        // Return the exact cart this guard just verified. Downstream checkout fences
        // may inspect it, but must not pay for a second sequential /cart.js read.
        return { canonicalId: canonicalId, legacyId: legacyId, cart: cart };
      });
    });
  }

  function ensureCanonicalCartContext() {
    if (inFlight) return inFlight;
    inFlight = performCartContextVerification().finally(function () {
      inFlight = null;
    });
    return inFlight;
  }

  function invalidate() {
    // Nothing durable is cached; kept for API compatibility with callers.
  }

  // Cached verification must not survive a store change (same tab or another tab).
  window.addEventListener('storeSelected', function () { invalidate(); prime(); });
  window.addEventListener('deliveryModeChanged', invalidate);
  window.addEventListener('storage', function (e) {
    if (!e || !e.key) return;
    if (e.key === 'selectedStoreId' || e.key === 'deliverySelected') invalidate();
  });

  // Warm the guard so most add-to-cart clicks await an already-settled promise
  // instead of paying for a round trip.
  // Warm the repair so the FIRST add is not slowed by it. This does not authorize
  // anything: every add still runs a fresh verification.
  function prime() {
    try {
      var loc = L();
      var seed = window.__SMOKEYS_SERVER_STORE_SEED || window.storeId || '';
      if (!loc || !loc.currentId || !loc.currentId(seed)) return;
      ensureCanonicalCartContext().catch(function () {});
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prime);
  } else {
    prime();
  }

  var api = {
    ensureCanonicalCartContext: ensureCanonicalCartContext,
    invalidate: invalidate,
    isVerified: function () { return false; }, // no durable authorization exists
  };
  window.SmokeysCartContext = api;

  // Named contract from the review. Attached defensively so the widget/source that
  // feature-detects `window.SmokeysLocation?.ensureCanonicalCartContext` works.
  if (window.SmokeysLocation) {
    window.SmokeysLocation.ensureCanonicalCartContext = ensureCanonicalCartContext;
  }
})();
